/**
 * Réglages persistants — Son (F-SETTINGS-001), Haptique (F-SETTINGS-002),
 * Animations (F-SETTINGS-003). État app-level unique, lu à la volée par `audio.ts`,
 * `haptics.ts`, `ui/fx.ts` et l'écran Réglages. Persistance localStorage `oc_v9_settings`.
 * Ne connaît NI les moteurs NI le réseau. Les fonctions pures (sanitize/clamp/resolve)
 * sont testables sans DOM.
 */

/** Trois positions d'animation : suit le système, forcé réduit, forcé complet. */
export type MotionMode = 'system' | 'reduced' | 'full';

export interface Settings {
  /** Sons de jeu actifs. */
  sound: boolean;
  /** Volume maître, 0..1. */
  volume: number;
  /** Retour haptique actif. */
  haptics: boolean;
  /** Mode d'animation. */
  motion: MotionMode;
}

const KEY = 'oc_v9_settings';

const DEFAULTS: Settings = {
  sound: true,
  volume: 0.6,
  haptics: true,
  motion: 'system',
};

let current: Settings = { ...DEFAULTS };

/** Borne le volume dans [0,1]. Pur. */
export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return DEFAULTS.volume;
  return Math.max(0, Math.min(1, v));
}

/** Normalise un objet inconnu (JSON relu) en `Settings` valides. Pur. */
export function sanitizeSettings(raw: unknown): Settings {
  const o = (raw ?? {}) as Partial<Record<keyof Settings, unknown>>;
  const motion: MotionMode = o.motion === 'reduced' || o.motion === 'full' ? o.motion : 'system';
  return {
    sound: typeof o.sound === 'boolean' ? o.sound : DEFAULTS.sound,
    volume: typeof o.volume === 'number' ? clampVolume(o.volume) : DEFAULTS.volume,
    haptics: typeof o.haptics === 'boolean' ? o.haptics : DEFAULTS.haptics,
    motion,
  };
}

/**
 * Le mouvement doit-il être réduit ? Pur. `reduced` force la coupe, `full` la désactive,
 * `system` suit la préférence OS (`prefers-reduced-motion`).
 */
export function resolveReducedMotion(mode: MotionMode, systemPrefersReduced: boolean): boolean {
  if (mode === 'reduced') return true;
  if (mode === 'full') return false;
  return systemPrefersReduced;
}

function systemPrefersReduced(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** État courant (lecture seule — muter via `setSetting`). Lu à la volée par les consommateurs. */
export function getSettings(): Readonly<Settings> {
  return current;
}

/** Le mouvement effectif est-il réduit à cet instant ? (setting + préférence OS). */
export function isMotionReduced(): boolean {
  return resolveReducedMotion(current.motion, systemPrefersReduced());
}

/** Reflète le mode d'animation sur `<html data-motion>` (piloté par le CSS de `theme.css`). */
export function applyMotion(): void {
  try {
    document.documentElement.dataset.motion = current.motion;
  } catch {
    /* pas de DOM (environnement de test) */
  }
}

/** Charge les réglages depuis localStorage (défauts si absent/illisible) et applique le motion. */
export function loadSettings(): void {
  let raw: unknown = {};
  try {
    raw = JSON.parse(localStorage.getItem(KEY) ?? '{}');
  } catch {
    raw = {};
  }
  current = sanitizeSettings(raw);
  applyMotion();
}

function save(): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* stockage indisponible : mode dégradé silencieux */
  }
}

/** Modifie un réglage, persiste, et applique le motion si concerné. */
export function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
  current = sanitizeSettings({ ...current, [key]: value });
  save();
  if (key === 'motion') applyMotion();
}
